import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import axios from "axios";
import { InfoCircle } from "iconsax-react";

// Imports from your project structure
import { commonConfigs, urls, doublewriteConfig } from "../../../config.js";
import beveatApiResourceOwner from "../../api/resource_owner_beveat_api.js";
import beveatApiStaging from "../../api/staging_beveat_api.js";
import beveatApiSandbox from "../../api/sandbox_beveat_api.js";
import beveatApiProduction from "../../api/production_beveat_api.js";

// Constants
import { DEVELOPER_PROJ_STATUS, DeveloperProjectStatusString } from "../../constants/developer_proj_status.jsx";
import { PLATFORM_SYSCODE, PlatformSyscodeString } from "../../constants/platform_syscode.jsx";
import { DEVELOPER_PROJ_TABLE_SYSCODE } from "../../constants/developer_proj_table_syscode.jsx";

const errorIcon = (
    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
        <InfoCircle className="h-5 w-5 text-red-500" aria-hidden="true" />
    </div>
);

export default function UpdateProjectModal({
    mode,           // "admin" | "developer" | "sandbox"
    setOpenModal,
    data,           // The project object (devProj or data)
    currentTab,     // Required for "admin" mode
    load,           // Required for "developer"/"sandbox" mode reload
    tableSyscode    // Required for API URL
}) {
    const [isDisabled, setIsDisabled] = useState(false);
    const [open, setOpen] = useState(true);

    const { register, handleSubmit, control, setValue, formState: { errors } } = useForm({
        defaultValues: {
            developer_proj_name: data?.developer_proj_name,
            developer_proj_website_url: data?.developer_proj_website_url || "",
            platform_syscode: data?.platform_syscode || null,
            // developer_proj_status: null
        }
    });

    // --- Configuration Mapping ---
    const isMode = {
        admin: mode === "admin",
        developer: mode === "developer",
        sandbox: mode === "sandbox"
    };

    const uiConfig = {
        title: isMode.admin ? "Selected Project Details" : isMode.sandbox ? "Update Sandbox" : "Update Community Project",
        submitLabel: isMode.admin && currentTab?.value === DEVELOPER_PROJ_STATUS.Pending_Release ? "Release" : "Update",
        api: isMode.sandbox ? beveatApiSandbox : beveatApiResourceOwner,
        header: isMode.sandbox ? commonConfigs.apiHeader : commonConfigs.resourceOwnerApiHeader
    };

    // --- Options Mapping ---
    const platformOptions = Object.entries(PLATFORM_SYSCODE).map(([key, value]) => ({
        value: value, label: PlatformSyscodeString[value]
    }));

    const statusOptions = Object.entries(DEVELOPER_PROJ_STATUS).map(([key, value]) => ({
        value: value,
        label: DeveloperProjectStatusString[value],
        isDisabled: value === currentTab?.value
    }));

    // --- Effects ---
    useEffect(() => {
        if (isMode.admin && currentTab?.value === DEVELOPER_PROJ_STATUS.Pending_Release) {
            setValue('developer_proj_status', DEVELOPER_PROJ_STATUS.Active);
        }
        // Set platform dropdown value if exists
        if (data?.platform_syscode) {
            const match = platformOptions.find(opt => opt.value === data.platform_syscode);
            if (match) setValue("platform_syscode", match);
        }
    }, [currentTab, data, setValue]);

    // --- Doublewrite Logic (Specific to Developer mode) ---
    const updateDoublewrite = async (body) => {
        if (!isMode.developer) return;
        const { url, headers } = doublewriteConfig(process.env.REACT_APP_ENV);
        const dwUrl = url + commonConfigs.apiUrls.UpdateDeveloperProject(data.developer_proj_id, tableSyscode);

        let apiInstance = axios;
        if (url === process.env.REACT_APP_BASE_OPENAPIPAAS_STAGING_URL) apiInstance = beveatApiStaging;
        else if (url === process.env.REACT_APP_BASE_OPENAPIPAAS_SANDBOX_URL) apiInstance = beveatApiSandbox;
        else if (url === process.env.REACT_APP_BASE_OPENAPIPAAS_PRODUCTION_URL) apiInstance = beveatApiProduction;

        try { await apiInstance.put(dwUrl, body, { headers }); }
        catch (e) { console.error("Doublewrite failed", e); }
    };

    // --- Form Submission ---
    const onSubmit = async (formData) => {
        setIsDisabled(true);

        let body = {};

        // --- Dynamic Body Construction ---
        if (isMode.admin) {
            body = {
                developer_proj_name: data.developer_proj_name, // Keeping original name
                developer_proj_status: formData.developer_proj_status,
                developer_proj_id: data.developer_proj_id
            };
        }
        else if (isMode.developer) {
            body = {
                // "access" likely refers to the platform_syscode value chosen
                access: null,
                developer_proj_name: formData.developer_proj_name,
                developer_proj_website_url: formData.developer_proj_website_url,
                developer_sso_id: localStorage.getItem("ssoId"),
                platform_syscode: formData.platform_syscode?.value || formData.platform_syscode,
                subscription_id: data.subscription_id, // Assuming this exists in the original data
            };
        }
        else if (isMode.sandbox) {
            body = {
                access: null,
                developer_proj_name: formData.developer_proj_name,
                developer_proj_website_url: formData.developer_proj_website_url,
                developer_sso_id: localStorage.getItem("ssoId"),
            };
        }

        // --- API Configuration ---
        const updateUrl = (isMode.sandbox ? urls.sandboxUrl : urls.resourceOwnerUrl) +
            commonConfigs.apiUrls.UpdateDeveloperProject(data.developer_proj_id, tableSyscode);

        uiConfig.api.put(updateUrl, body, { headers: uiConfig.header })
            .then(async (res) => {
                if (isMode.developer) await updateDoublewrite(body);
                alert("Success");
                setOpenModal(false);
                if (load) load(true);
                window.location.reload();
            })
            .catch((err) => {
                alert(`Error: ${err.message}`);
                setIsDisabled(false);
            });
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" onClose={() => setOpenModal(false)}>
                <div className="flex items-center justify-center min-h-screen">
                    <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <Dialog.Overlay className="fixed inset-0 bg-gray-500 opacity-75" onClick={() => setOpenModal(false)} />
                    </Transition.Child>

                    <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 translate-y-4 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100">
                        <div className="inline-block bg-white rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full sm:p-6">
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <Dialog.Title as="h3" className="text-lg font-medium text-gray-900 mb-6">{uiConfig.title}</Dialog.Title>

                                {/* PROJECT NAME */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700">Project Name</label>
                                    <div className="relative mt-1">
                                        <input
                                            type="text"
                                            {...register("developer_proj_name", { required: true })}
                                            readOnly={isMode.admin}
                                            className={`block w-full border-gray-300 rounded-md sm:text-sm ${isMode.admin ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
                                        />
                                        {errors.developer_proj_name && errorIcon}
                                    </div>
                                </div>

                                {/* WEBSITE URL (Developer & Sandbox only) */}
                                {!isMode.admin && (
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700">Project Website URL</label>
                                        <input
                                            type="text"
                                            {...register("developer_proj_website_url", { required: !isMode.developer })}
                                            className="mt-1 block w-full border-gray-300 rounded-md sm:text-sm"
                                        />
                                    </div>
                                )}

                                {/* PLATFORM TYPE (Developer only) */}
                                {isMode.developer && (
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700">Platform Type</label>
                                        <Controller
                                            name="platform_syscode"
                                            control={control}
                                            render={({ field }) => (
                                                <Select {...field} options={platformOptions} className="mt-1 text-sm" />
                                            )}
                                        />
                                    </div>
                                )}

                                {/* PROJECT STATUS (Admin only) */}
                                {isMode.admin && (
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700">Project Status</label>
                                        <Controller
                                            name="developer_proj_status"
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    options={statusOptions}
                                                    className="mt-1 text-sm"
                                                    onChange={(val) => field.onChange(val?.value)}
                                                    value={statusOptions.find(opt => opt.value === field.value)}
                                                />
                                            )}
                                        />
                                    </div>
                                )}

                                {/* BUTTONS */}
                                <div className="mt-6 flex justify-end space-x-3">
                                    <button type="button" onClick={() => setOpenModal(false)} className="px-4 py-2 text-red-500 font-bold border border-red-500 rounded-md hover:bg-red-500 hover:text-white">Cancel</button>
                                    <button type="submit" disabled={isDisabled} className="px-4 py-2 text-blue-500 font-bold border border-blue-500 rounded-md hover:bg-blue-500 hover:text-white">
                                        {uiConfig.submitLabel}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
}